// =============================================================================
// STORYBOOK HERO COMPONENT
// =============================================================================
// A hero component that displays multiple components as book pages with
// simple left-to-right navigation

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import type { ContentComponent } from '@/types';
import { ContentRenderer } from '@/components/ContentRenderer';
import './StorybookHero.scss';

interface StorybookHeroProps {
  component: ContentComponent;
}

/**
 * Storybook Hero Component
 * Displays components as book pages with left-to-right navigation
 */
export const StorybookHero: React.FC<StorybookHeroProps> = ({ component }) => {
  const { id, metadata } = component;
  
  // Get pages from metadata - each page is a full component
  const pages: ContentComponent[] = metadata?.pages || [];
  
  
  // Initialize currentPage from URL hash if present
  // Note: #hero-home is treated as first page (index 0) with no hash
  const getInitialPage = (): number => {
    if (typeof window === 'undefined') return 0;
    const hash = window.location.hash.replace('#', '');
    if (hash) {
      // If hash is hero-home, treat as first page (will remove hash on mount)
      if (hash === 'hero-home') {
        return 0;
      }
      const pageIndex = pages.findIndex(page => page.id === hash);
      return pageIndex >= 0 ? pageIndex : 0;
    }
    return 0;
  };

  const [currentPage, setCurrentPage] = useState(getInitialPage);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  
  // Refs for touch handlers
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  const currentPageRef = useRef<number>(currentPage);
  const isTransitioningRef = useRef<boolean>(false);
  
  // Navigate to a specific page
  const goToPage = useCallback((pageIndex: number, updateHash = true) => {
    if (pageIndex < 0 || pageIndex >= pages.length || isTransitioning) return;
    
    setIsTransitioning(true);
    
    // Update URL hash for deep linking
    if (updateHash && typeof window !== 'undefined' && pages[pageIndex]) {
      if (pageIndex === 0) {
        window.history.replaceState(null, '', window.location.pathname);
      } else {
        window.history.replaceState(null, '', `#${pages[pageIndex].id}`);
      }
    }
    
    setTimeout(() => {
      setCurrentPage(pageIndex);
      setIsTransitioning(false);
    }, 400); // Match animation duration
  }, [pages, isTransitioning]);
  
  // Handle hash changes for deep linking
  useEffect(() => {
    const handleHashChange = () => {
      if (typeof window === 'undefined') return;
      const hash = window.location.hash.replace('#', '');
      if (hash) {
        const pageIndex = pages.findIndex(page => page.id === hash);
        if (pageIndex >= 0 && pageIndex !== currentPage && !isTransitioning) {
          goToPage(pageIndex, false);
        }
      } else {
        if (currentPage !== 0 && !isTransitioning) {
          goToPage(0, false);
        }
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [pages, currentPage, isTransitioning, goToPage]);
  
  // Initialize URL on mount
  useEffect(() => {
    if (typeof window === 'undefined' || pages.length === 0) return;
    const hash = window.location.hash.replace('#', '');
    if (hash === 'hero-home') {
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, []);
  
  // Ensure first page has no hash
  useEffect(() => {
    if (typeof window === 'undefined' || pages.length === 0) return;
    if (currentPage === 0 && window.location.hash) {
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, [currentPage, pages.length]);

  // Keep refs in sync
  useEffect(() => {
    currentPageRef.current = currentPage;
    isTransitioningRef.current = isTransitioning;
  }, [currentPage, isTransitioning]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (isTransitioning) return;
      
      if (e.key === 'ArrowLeft' && currentPage > 0) {
        goToPage(currentPage - 1);
      } else if (e.key === 'ArrowRight' && currentPage < pages.length - 1) {
        goToPage(currentPage + 1);
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        const event = new CustomEvent('toggleFooter', { bubbles: true });
        window.dispatchEvent(event);
        document.dispatchEvent(event);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentPage, pages.length, isTransitioning, goToPage]);

  // Simple touch swipe handlers
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper || pages.length === 0) return;

    let startX = 0;
    let startY = 0;

    const handleTouchStart = (e: TouchEvent) => {
      if (isTransitioningRef.current) return;
      const touch = e.touches[0];
      if (!touch) return;
      startX = touch.clientX;
      startY = touch.clientY;
      touchStartRef.current = { x: startX, y: startY };
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!touchStartRef.current || isTransitioningRef.current) return;
      const touch = e.touches[0];
      if (!touch) return;
      
      const deltaX = Math.abs(touch.clientX - startX);
      const deltaY = Math.abs(touch.clientY - startY);
      
      // Prevent scrolling if horizontal swipe detected
      if (deltaX > 10 && deltaX > deltaY) {
        e.preventDefault();
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (!touchStartRef.current) return;
      
      const touch = e.changedTouches[0];
      if (!touch) {
        touchStartRef.current = null;
        return;
      }
      
      const deltaX = startX - touch.clientX; // Positive = left swipe
      const deltaY = Math.abs(touch.clientY - startY);
      const swipeDistance = Math.abs(deltaX);
      
      touchStartRef.current = null;
      
      // Require 50px horizontal swipe, horizontal > vertical
      if (swipeDistance < 50 || swipeDistance <= deltaY) return;
      
      const pageIndex = currentPageRef.current;
      if (deltaX > 0 && pageIndex < pages.length - 1) {
        goToPage(pageIndex + 1);
      } else if (deltaX < 0 && pageIndex > 0) {
        goToPage(pageIndex - 1);
      }
    };

    wrapper.addEventListener('touchstart', handleTouchStart, { passive: false });
    wrapper.addEventListener('touchmove', handleTouchMove, { passive: false });
    wrapper.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      wrapper.removeEventListener('touchstart', handleTouchStart);
      wrapper.removeEventListener('touchmove', handleTouchMove);
      wrapper.removeEventListener('touchend', handleTouchEnd);
    };
  }, [pages.length, goToPage]);

  // Navigation helpers
  const nextPage = () => {
    if (currentPage < pages.length - 1) {
      goToPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 0) {
      goToPage(currentPage - 1);
    }
  };

  const scrollToFooter = () => {
    const event = new CustomEvent('toggleFooter', { bubbles: true });
    window.dispatchEvent(event);
    document.dispatchEvent(event);
  };

  if (pages.length === 0) {
    return null;
  }

  const showPrevButton = currentPage > 0;
  const showNextButton = currentPage < pages.length - 1;

  return (
    <section 
      id={id} 
      className="storybook-hero"
    >
      <div className="storybook-container">
        {/* Page Container - Full Width */}
        <div 
          ref={wrapperRef}
          className="storybook-pages-wrapper"
        >
          {pages.map((page, index) => {
            const isActive = index === currentPage;
            const transform = `translateX(${(index - currentPage) * 100}%)`;
            
            return (
              <div
                key={page.id}
                className={`storybook-page ${isActive ? 'active' : ''}`}
                style={{ transform }}
              >
                <div className="storybook-page-content">
                  <ContentRenderer components={[page]} />
                </div>
              </div>
            );
          })}
        </div>

        {/* Navigation Arrows */}
        {showPrevButton && (
          <button
            className="storybook-nav-arrow storybook-nav-arrow-left"
            onClick={prevPage}
            aria-label="Previous page"
            disabled={isTransitioning}
            type="button"
          >
            <ChevronLeft size={32} />
          </button>
        )}

        {showNextButton && (
          <button
            className="storybook-nav-arrow storybook-nav-arrow-right"
            onClick={nextPage}
            aria-label="Next page"
            disabled={isTransitioning}
            type="button"
          >
            <ChevronRight size={32} />
          </button>
        )}

        {/* Down Arrow for Footer */}
        <button
          className="storybook-nav-arrow storybook-nav-arrow-down"
          onClick={scrollToFooter}
          aria-label="Scroll to footer"
          type="button"
        >
          <ChevronDown size={32} />
        </button>
      </div>
    </section>
  );
};

export default StorybookHero;

